import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { CalendarIcon, Plus, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProspectOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    currentEmployer: '',
    minSalary: '',
    maxSalary: '',
    noticePeriod: '',
    about: '',
    currentPosition: '',
    preferredPositions: [],
    skills: [],
    industries: [],
    languages: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Reference data
  const [positions, setPositions] = useState([]);
  const [skills, setSkills] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [languages, setLanguages] = useState([]);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  const totalSteps = 4;
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Fetch reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [positionsRes, skillsRes, industriesRes, languagesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/positions`),
          axios.get(`${BACKEND_URL}/api/skills`),
          axios.get(`${BACKEND_URL}/api/industries`),
          axios.get(`${BACKEND_URL}/api/languages`)
        ]);

        setPositions(positionsRes.data.positions || []);
        setSkills(skillsRes.data.skills || []);
        setIndustries(industriesRes.data.industries || []);
        setLanguages(languagesRes.data.languages || []);
      } catch (error) {
        console.error('Failed to fetch reference data:', error);
      }
    };

    fetchReferenceData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMultiSelectAdd = (field, itemId) => {
    if (!formData[field].includes(itemId)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], itemId]
      }));
    }
  };

  const handleMultiSelectRemove = (field, itemId) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(id => id !== itemId)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.phone) newErrors.phone = 'Phone number is required';
        break;
      case 2:
        if (!formData.currentEmployer) newErrors.currentEmployer = 'Current employer is required';
        if (!formData.minSalary) newErrors.minSalary = 'Minimum salary is required';
        if (!formData.maxSalary) newErrors.maxSalary = 'Maximum salary is required';
        if (formData.minSalary && formData.maxSalary && parseInt(formData.minSalary) > parseInt(formData.maxSalary)) {
          newErrors.maxSalary = 'Maximum salary must be greater than minimum salary';
        }
        break;
      case 3:
        if (formData.skills.length === 0) newErrors.skills = 'Please select at least one skill';
        if (formData.industries.length === 0) newErrors.industries = 'Please select at least one industry';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/prospects`, formData);
      navigate('/dashboard');
    } catch (error) {
      setErrors({ submit: error.response?.data?.message || 'Failed to complete onboarding' });
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <Card>
      <CardHeader>
        <CardTitle>Personal Information</CardTitle>
        <CardDescription>
          Let's start with some basic information about you
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={errors.firstName ? 'border-red-500' : ''}
              data-testid="first-name-input"
            />
            {errors.firstName && (
              <p className="text-sm text-red-500">{errors.firstName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={errors.lastName ? 'border-red-500' : ''}
              data-testid="last-name-input"
            />
            {errors.lastName && (
              <p className="text-sm text-red-500">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            className={errors.phone ? 'border-red-500' : ''}
            data-testid="phone-input"
          />
          {errors.phone && (
            <p className="text-sm text-red-500">{errors.phone}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender">Gender</Label>
            <Select value={formData.gender} onValueChange={(value) => handleSelectChange('gender', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="NonBinary">Non-binary</SelectItem>
                <SelectItem value="PreferNotToSay">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderProfessionalInfo = () => (
    <Card>
      <CardHeader>
        <CardTitle>Professional Information</CardTitle>
        <CardDescription>
          Tell us about your current role and salary expectations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="currentEmployer">Current Employer *</Label>
          <Input
            id="currentEmployer"
            name="currentEmployer"
            value={formData.currentEmployer}
            onChange={handleInputChange}
            className={errors.currentEmployer ? 'border-red-500' : ''}
            placeholder="e.g. Google, Microsoft"
          />
          {errors.currentEmployer && (
            <p className="text-sm text-red-500">{errors.currentEmployer}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="currentPosition">Current Position</Label>
          <Select value={formData.currentPosition} onValueChange={(value) => handleSelectChange('currentPosition', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select current position" />
            </SelectTrigger>
            <SelectContent>
              {positions.map(position => (
                <SelectItem key={position.id} value={position.id}>
                  {position.title} {position.level && `(${position.level})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="minSalary">Minimum Salary Expectation *</Label>
            <Input
              id="minSalary"
              name="minSalary"
              type="number"
              value={formData.minSalary}
              onChange={handleInputChange}
              className={errors.minSalary ? 'border-red-500' : ''}
              placeholder="50000"
            />
            {errors.minSalary && (
              <p className="text-sm text-red-500">{errors.minSalary}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxSalary">Maximum Salary Expectation *</Label>
            <Input
              id="maxSalary"
              name="maxSalary"
              type="number"
              value={formData.maxSalary}
              onChange={handleInputChange}
              className={errors.maxSalary ? 'border-red-500' : ''}
              placeholder="80000"
            />
            {errors.maxSalary && (
              <p className="text-sm text-red-500">{errors.maxSalary}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="noticePeriod">Notice Period (days)</Label>
          <Input
            id="noticePeriod"
            name="noticePeriod"
            type="number"
            value={formData.noticePeriod}
            onChange={handleInputChange}
            placeholder="30"
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderSkillsAndIndustries = () => (
    <Card>
      <CardHeader>
        <CardTitle>Skills & Industries</CardTitle>
        <CardDescription>
          Select your skills and preferred industries
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label>Skills *</Label>
            <Select onValueChange={(value) => handleMultiSelectAdd('skills', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Add skills" />
              </SelectTrigger>
              <SelectContent>
                {skills.filter(skill => !formData.skills.includes(skill.id)).map(skill => (
                  <SelectItem key={skill.id} value={skill.id}>
                    {skill.name} {skill.category && `(${skill.category})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.skills.map(skillId => {
                const skill = skills.find(s => s.id === skillId);
                return skill ? (
                  <Badge key={skillId} variant="secondary">
                    {skill.name}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={() => handleMultiSelectRemove('skills', skillId)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
            {errors.skills && (
              <p className="text-sm text-red-500 mt-1">{errors.skills}</p>
            )}
          </div>

          <div>
            <Label>Industries *</Label>
            <Select onValueChange={(value) => handleMultiSelectAdd('industries', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Add industries" />
              </SelectTrigger>
              <SelectContent>
                {industries.filter(industry => !formData.industries.includes(industry.id)).map(industry => (
                  <SelectItem key={industry.id} value={industry.id}>
                    {industry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.industries.map(industryId => {
                const industry = industries.find(i => i.id === industryId);
                return industry ? (
                  <Badge key={industryId} variant="secondary">
                    {industry.name}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={() => handleMultiSelectRemove('industries', industryId)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
            {errors.industries && (
              <p className="text-sm text-red-500 mt-1">{errors.industries}</p>
            )}
          </div>

          <div>
            <Label>Preferred Positions</Label>
            <Select onValueChange={(value) => handleMultiSelectAdd('preferredPositions', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Add preferred positions" />
              </SelectTrigger>
              <SelectContent>
                {positions.filter(position => !formData.preferredPositions.includes(position.id)).map(position => (
                  <SelectItem key={position.id} value={position.id}>
                    {position.title} {position.level && `(${position.level})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.preferredPositions.map(positionId => {
                const position = positions.find(p => p.id === positionId);
                return position ? (
                  <Badge key={positionId} variant="secondary">
                    {position.title}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={() => handleMultiSelectRemove('preferredPositions', positionId)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
          </div>

          <div>
            <Label>Languages</Label>
            <Select onValueChange={(value) => handleMultiSelectAdd('languages', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Add languages" />
              </SelectTrigger>
              <SelectContent>
                {languages.filter(language => !formData.languages.includes(language.id)).map(language => (
                  <SelectItem key={language.id} value={language.id}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.languages.map(languageId => {
                const language = languages.find(l => l.id === languageId);
                return language ? (
                  <Badge key={languageId} variant="secondary">
                    {language.name}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={() => handleMultiSelectRemove('languages', languageId)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderAbout = () => (
    <Card>
      <CardHeader>
        <CardTitle>About Yourself</CardTitle>
        <CardDescription>
          Tell us more about yourself and your career goals
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="about">Professional Summary</Label>
          <Textarea
            id="about"
            name="about"
            value={formData.about}
            onChange={handleInputChange}
            placeholder="Tell us about your experience, achievements, and career goals..."
            className="min-h-32"
          />
          <p className="text-xs text-gray-500">
            This will help recruiters understand your background better
          </p>
        </div>

        {errors.submit && (
          <Alert variant="destructive">
            <AlertDescription>{errors.submit}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderPersonalInfo();
      case 2:
        return renderProfessionalInfo();
      case 3:
        return renderSkillsAndIndustries();
      case 4:
        return renderAbout();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to JobastiIQ, {user?.username}!
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Let's complete your profile to help recruiters find you
          </p>
          
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Step {currentStep} of {totalSteps}</span>
              <span>{Math.round(progressPercentage)}% Complete</span>
            </div>
            <Progress value={progressPercentage} className="w-full" />
          </div>
        </div>

        {renderCurrentStep()}

        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            data-testid="previous-button"
          >
            Previous
          </Button>

          {currentStep < totalSteps ? (
            <Button onClick={handleNext} data-testid="next-button">
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              data-testid="complete-button"
            >
              {loading ? 'Completing...' : 'Complete Profile'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectOnboarding;